// frontend/src/components/Navigation.js
import React, { useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSupport } from '../context/SupportContext';
import { useTenant } from '../context/TenantContext';
import { useLocation } from '../contexts/LocationContext';
import { useControllerBays } from '../hooks/useControllerBays';
import LocationSelector from './LocationSelector';
import DeveloperOrgSelector from './DeveloperOrgSelector';
import { api } from '../services/api';
import { companyPageAllowed } from '../utils/companyAccess';
import useOwnerSharingAccounts from '../hooks/useOwnerSharingAccounts';
import './Navigation.css';

function Navigation() {
  const { currentUser, logout, isAdmin, isDeveloper, userRole, userData, companyView, companyOrganizations = [] } = useAuth();
  const { colors, logoUrl, appName, navigation } = useTenant();
  const { selectedLocationId } = useLocation();
  const { hasController: hasControllerBays } = useControllerBays(selectedLocationId);
  const { enabled: supportEnabled, developer: supportDeveloper, unreadCount: supportUnreadCount } = useSupport();
  const managesSharing = useOwnerSharingAccounts(currentUser?.uid, userRole);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const statisticsPrefetching = useRef(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // DISABLED: Prefetch was competing with page loads and causing slowness
  // The pages now prefetch their own data after initial load
  // React.useEffect(() => {
  //   if (washDataPrefetched.current) return;
  //   if (!selectedLocationId || !selectedOrgId) return;
  //   washDataPrefetched.current = true;
  //   const filters = { locationId: selectedLocationId, orgId: selectedOrgId };
  //   api.getWashDetectionRecentAll(240, filters).catch(() => {});
  //   api.getRecentEvents(4).catch(() => {});
  // }, [selectedLocationId, selectedOrgId]);

  // Prefetch statistics data when user hovers over Statistics link
  const prefetchStatistics = () => {
    if (statisticsPrefetching.current) return;
    statisticsPrefetching.current = true;
    api.getBulkStatistics(null, selectedLocationId)
      .catch(() => {})
      .finally(() => { statisticsPrefetching.current = false; });
  };


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Apply tenant navigation customizations
  const hiddenItems = navigation?.hiddenItems || [];
  const customLabels = navigation?.customLabels || {};

  const getLabel = (path, defaultLabel) => customLabels[path] || defaultLabel;
  // Menu availability is controlled by role and company access, not by tenant
  // configuration. Keep accepting hiddenItems in legacy tenant payloads, but
  // ignore it so tenant settings cannot remove navigation entries.
  const isHidden = (path) => !companyPageAllowed(companyView, userData, path);
  // Dropdowns list the same items for every admin. Items the company-access
  // view cannot open stay in place but are greyed out and not clickable.

  // Check if current path is in a dropdown group
  const isInGroup = (items) => items.some(item => !item.disabled && routerLocation.pathname === item.path);

  // Build dropdown groups
  const inventoryItems = [
    { path: '/barrel-inventory', label: getLabel('/barrel-inventory', 'Barrel Inventory') },
    { path: '/facility-inventory', label: getLabel('/facility-inventory', 'Facility Inventory') },
  ].map(item => ({ ...item, disabled: isHidden(item.path) }));

  // Check if user is manager or above (manager, admin, owner, superuser, developer)
  const isManagerOrAbove = ['developer', 'superuser', 'owner', 'admin', 'manager'].includes(userRole);

  // Build "Manager" items - visible to manager and above
  const managerItems = [];
  if (isManagerOrAbove) {
    managerItems.push({ path: '/wash-packages', label: getLabel('/wash-packages', 'Wash Package Usage'), disabled: isHidden('/wash-packages') });
    managerItems.push({ path: '/wash-packages-cost', label: getLabel('/wash-packages-cost', 'Wash Package Cost'), disabled: isHidden('/wash-packages-cost') });
    managerItems.push({ path: '/cost-per-wash', label: getLabel('/cost-per-wash', 'Cost Per Wash'), disabled: isHidden('/cost-per-wash') });
    managerItems.push({ path: '/chem-usage', label: getLabel('/chem-usage', 'Order Management'), disabled: isHidden('/chem-usage') });
    managerItems.push({ path: '/graphs', label: getLabel('/graphs', 'Graphs'), disabled: isHidden('/graphs') });
    managerItems.push({ path: '/statistics', label: getLabel('/statistics', 'Statistics'), onMouseEnter: prefetchStatistics, disabled: isHidden('/statistics') });
    managerItems.push({ path: '/admin/average-volume', label: getLabel('/admin/average-volume', 'Average Volume'), disabled: isHidden('/admin/average-volume') });
    managerItems.push({ path: '/admin/daily-revenue', label: getLabel('/admin/daily-revenue', 'Daily Revenue'), disabled: isHidden('/admin/daily-revenue') });
    managerItems.push({ path: '/wash-count', label: getLabel('/wash-count', 'Wash Count Report'), disabled: isHidden('/wash-count') });
  }

  // Build "Admin" items - admin-only
  const adminItems = [];

  // Every role: Tips and Activity Log, so anyone can record tip changes and
  // titrations and see what happened at the wash.
  if (isAdmin) {
    adminItems.push({ path: '/admin', label: getLabel('/admin', 'Settings'), disabled: isHidden('/admin') });
  }
  adminItems.push({ path: '/admin/injector-tips', label: getLabel('/admin/injector-tips', 'Tips'), disabled: isHidden('/admin/injector-tips') });
  adminItems.push({ path: '/audit-log', label: getLabel('/audit-log', 'Activity Log'), disabled: isHidden('/audit-log') });

  // Admin-only items (Billing). Partner Access (old whole-account grants) was
  // removed 2026-10-04; sharing is Data Sharing below.
  if (isAdmin) {
    adminItems.push({ path: '/admin/billing', label: getLabel('/admin/billing', 'Billing'), disabled: isHidden('/admin/billing') });
  }

  // Data Sharing - admins of a company that owns at least one location
  if (managesSharing) {
    adminItems.push({ path: '/data-sharing', label: getLabel('/data-sharing', 'Data Sharing'), disabled: isHidden('/data-sharing') });
  }

  // Integrations - accessible to admins OR users with Dencar permission
  const hasDencarPermission = userData?.permissions?.dencar?.configure === true;
  if (isAdmin || hasDencarPermission) {
    adminItems.push({ path: '/admin/integrations', label: getLabel('/admin/integrations', 'Integrations'), disabled: isHidden('/admin/integrations') });
  }

  // Build "Experimental" items - features in development, admin-only
  const experimentalItems = [];
  if (isAdmin) {
    experimentalItems.push({ path: '/admin/activity-report', label: getLabel('/admin/activity-report', 'Activity Report'), disabled: isHidden('/admin/activity-report') });
    experimentalItems.push({ path: '/admin/reference-library', label: getLabel('/admin/reference-library', 'Reference Library'), disabled: isHidden('/admin/reference-library') });
    experimentalItems.push({ path: '/admin/checklists', label: getLabel('/admin/checklists', 'Checklists'), disabled: isHidden('/admin/checklists') });
    experimentalItems.push({ path: '/admin/env-sensors', label: getLabel('/admin/env-sensors', 'Env Sensors'), disabled: isHidden('/admin/env-sensors') });
    experimentalItems.push({ path: '/admin/data-cleanup', label: getLabel('/admin/data-cleanup', 'Data Cleanup'), disabled: isHidden('/admin/data-cleanup') });
    experimentalItems.push({ path: '/admin/wash-package-counts', label: getLabel('/admin/wash-package-counts', 'Wash Package Counts'), disabled: isHidden('/admin/wash-package-counts') });
    experimentalItems.push({ path: '/admin/raw-pulses', label: getLabel('/admin/raw-pulses', 'Raw Pulses'), disabled: isHidden('/admin/raw-pulses') });
    experimentalItems.push({ path: '/admin/estimated-dilution', label: getLabel('/admin/estimated-dilution', 'Estimated Dilution'), disabled: isHidden('/admin/estimated-dilution') });
    // Locations with a bay reading its wash controller (Settings -> Wash Equipment Integration), and demo sites.
    if (hasControllerBays) {
      experimentalItems.push({ path: '/admin/wash-replay', label: getLabel('/admin/wash-replay', 'Wash Replay'), disabled: isHidden('/admin/wash-replay') });
    }
  }

  // Dropdown component
  const NavDropdown = ({ label, items, id }) => {
    if (items.length === 0) return null;
    const isOpen = openDropdown === id;
    const isActive = isInGroup(items);

    return (
      <div className="nav-dropdown" style={{ position: 'relative' }}>
        <button
          className="nav-tab nav-dropdown-toggle"
          onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
          style={{
            ...styles.tab,
            ...(isActive ? styles.activeTab : {}),
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {label}
          <span style={{ fontSize: '10px' }}>▼</span>
        </button>
        {isOpen && (
          <div className="nav-dropdown-menu" style={styles.dropdownMenu}>
            {items.map(item => item.disabled ? (
              <span
                key={item.path}
                aria-disabled="true"
                title="Not available with granted access"
                style={{ ...styles.dropdownItem, opacity: 0.4, cursor: 'not-allowed' }}
              >
                {item.label}
              </span>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setOpenDropdown(null)}
                onMouseEnter={item.onMouseEnter}
                style={({ isActive }) => ({
                  ...styles.dropdownItem,
                  ...(isActive ? styles.dropdownItemActive : {})
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Generate styles from tenant colors
  const styles = getStyles(colors);

  return (
    <nav style={styles.nav}>
      <div style={styles.navContent} className="nav-container">
        {/* Tenant Logo */}
        {logoUrl && (
          <div style={styles.logoContainer} className="nav-logo-container">
            <img
              src={logoUrl}
              alt={appName}
              style={styles.logo}
              className="nav-logo"
            />
          </div>
        )}
        <div style={styles.tabsContainer} className="nav-tabs-container">
          {/* Home */}
          {!isHidden('/home') && (
            <NavLink
              to="/home"
              className="nav-tab"
              style={({ isActive }) => ({
                ...styles.tab,
                ...(isActive ? styles.activeTab : {})
              })}
            >
              {getLabel('/home', 'Home')}
            </NavLink>
          )}

          {/* Tablet View - after Home */}
          {!isHidden('/tablet-view') && (
            <NavLink
              to="/tablet-view"
              className="nav-tab"
              style={({ isActive }) => ({
                ...styles.tab,
                ...(isActive ? styles.activeTab : {})
              })}
            >
              {getLabel('/tablet-view', 'Tablet View')}
            </NavLink>
          )}

          {/* Chemical Usage */}
          {!isHidden('/dashboard') && (
            <NavLink
              to="/dashboard"
              className="nav-tab"
              style={({ isActive }) => ({
                ...styles.tab,
                ...(isActive ? styles.activeTab : {})
              })}
            >
              {getLabel('/dashboard', 'Chemical Usage')}
            </NavLink>
          )}

          {/* Inventory Dropdown */}
          <NavDropdown label="Inventory" items={inventoryItems} id="inventory" />

          {/* Manager Dropdown - visible to manager and above */}
          {managerItems.length > 0 && (
            <NavDropdown label="Manager" items={managerItems} id="manager" />
          )}

          {/* Admin Dropdown - visible to admin and above */}
          {adminItems.length > 0 && (
            <NavDropdown label="Admin" items={adminItems} id="admin" />
          )}

          {/* Experimental Dropdown - features in development */}
          {experimentalItems.length > 0 && (
            <NavDropdown label="Experimental" items={experimentalItems} id="experimental" />
          )}

          {/* Developer Console */}
          {isDeveloper && !isHidden('/developer') && (
            <NavLink
              to="/developer"
              className="nav-tab"
              style={({ isActive }) => ({
                ...styles.tab,
                ...(isActive ? styles.activeTab : {})
              })}
            >
              {getLabel('/developer', 'Developer Console')}
            </NavLink>
          )}
        </div>
        <div style={styles.userSection} className="nav-user-section">
          {currentUser && (
            <>
              {supportEnabled && !isHidden('/support') && (
                <NavLink
                  to={supportDeveloper ? '/developer/support' : '/support'}
                  aria-label={`Support${supportUnreadCount > 0 ? `, ${supportUnreadCount} unread` : ''}`}
                  style={({ isActive }) => ({
                    ...styles.supportLink,
                    ...(isActive ? styles.supportLinkActive : {})
                  })}
                >
                  <MessageCircle size={17} aria-hidden="true" />
                  <span>Help</span>
                  {supportUnreadCount > 0 && (
                    <span style={styles.supportBadge}>{supportUnreadCount > 99 ? '99+' : supportUnreadCount}</span>
                  )}
                </NavLink>
              )}
              {(isDeveloper || companyOrganizations.length > 1) && <DeveloperOrgSelector />}
              <LocationSelector />
              <button onClick={handleLogout} style={styles.logoutButton} className="nav-logout-button">
                Logout
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// Styles are now generated dynamically based on tenant colors
const getStyles = (colors) => ({
  nav: {
    backgroundColor: colors.navBackground,
    padding: '0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  navContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
  },
  logoContainer: {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    borderRight: `1px solid ${colors.navText}22`,
  },
  logo: {
    maxHeight: '36px',
    maxWidth: '140px',
    objectFit: 'contain',
  },
  tabsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
  },
  tab: {
    color: colors.navText,
    padding: '12px 20px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.3s',
    borderBottom: '3px solid transparent',
  },
  activeTab: {
    backgroundColor: colors.navBackground === '#333333' || colors.navBackground === '#333'
      ? '#555'
      : adjustBrightness(colors.navBackground, 20),
    borderBottom: `3px solid ${colors.navActiveIndicator}`,
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
  },
  supportLink: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '32px',
    padding: '5px 9px',
    borderRadius: '6px',
    color: colors.navText,
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  supportLinkActive: {
    backgroundColor: colors.navText + '22',
  },
  supportBadge: {
    minWidth: '18px',
    height: '18px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    borderRadius: '999px',
    backgroundColor: '#dc2626',
    color: '#fff',
    fontSize: '10px',
    lineHeight: 1,
  },
  userEmail: {
    color: colors.navText + 'cc', // slightly transparent
    fontSize: '13px',
  },
  logoutButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '6px 16px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: colors.navBackground,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    borderRadius: '0 0 4px 4px',
    minWidth: '180px',
    zIndex: 1000,
    overflow: 'hidden',
  },
  dropdownItem: {
    display: 'block',
    color: colors.navText,
    padding: '10px 16px',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500',
    borderBottom: `1px solid ${colors.navText}22`,
    transition: 'background-color 0.2s',
  },
  dropdownItemActive: {
    backgroundColor: colors.navBackground === '#333333' || colors.navBackground === '#333'
      ? '#555'
      : adjustBrightness(colors.navBackground, 20),
  }
});

// Helper to lighten/darken a hex color
function adjustBrightness(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

export default Navigation;
